(() => {
  'use strict';

  let audioContext = null;
  let soundBusyUntil = -Infinity;
  let pageGeneration = 0;
  const pendingHooks = new WeakMap();
  const activeHooks = new Set();
  const activeVoices = new Set();
  const debug = new URLSearchParams(window.location.search).get('dripstock_sound_debug') === '1';

  function report(stage) {
    if (debug) console.info('[Dripstock sound] ' + stage);
  }

  function stopVoices() {
    for (const node of activeVoices) {
      try { node.oscillator.stop(); } catch (_) {}
      try { node.oscillator.disconnect(); } catch (_) {}
      try { node.gain.disconnect(); } catch (_) {}
    }
    activeVoices.clear();
    soundBusyUntil = -Infinity;
  }

  function armAudio() {
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return null;
      if (!audioContext || audioContext.state === 'closed') {
        const context = new Context();
        audioContext = context;
        context.onstatechange = () => {
          if (audioContext === context && context.state !== 'running') stopVoices();
        };
      }
      if (audioContext.state === 'suspended' || audioContext.state === 'interrupted') {
        stopVoices();
        audioContext.resume().catch(() => {});
      }
      return audioContext;
    } catch (_) {
      return null;
    }
  }

  function playChime(buyNow) {
    if (window.DripstockSound && !window.DripstockSound.enabled()) return;
    const now = performance.now();
    const context = audioContext;
    if (!context || context.state !== 'running' || now < soundBusyUntil || document.visibilityState !== 'visible') {
      return;
    }

    const nodes = [];
    try {
      const start = context.currentTime + 0.003;
      const notes = buyNow ? [
        { offset: 0, frequency: 620, end: 440, duration: 0.065, volume: 0.035, type: 'triangle' },
        { offset: 0.05, frequency: 1568, end: 1568, duration: 0.44, volume: 0.035, type: 'sine' },
        { offset: 0.055, frequency: 2352, end: 2352, duration: 0.425, volume: 0.016, type: 'sine' },
      ] : [
        { offset: 0, frequency: 523.25, end: 523.25, duration: 0.11, volume: 0.050, type: 'sine' },
        { offset: 0.068, frequency: 783.99, end: 783.99, duration: 0.14, volume: 0.042, type: 'sine' },
        { offset: 0.16, frequency: 1046.50, end: 1046.50, duration: 0.19, volume: 0.032, type: 'sine' },
      ];

      for (const note of notes) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const begin = start + note.offset;
        const node = { oscillator, gain, begin, end: begin + note.duration + 0.007 };
        nodes.push(node);
        activeVoices.add(node);
        oscillator.type = note.type;
        oscillator.frequency.setValueAtTime(note.frequency, begin);
        oscillator.frequency.exponentialRampToValueAtTime(note.end, begin + note.duration);
        gain.gain.setValueAtTime(0.0001, begin);
        gain.gain.exponentialRampToValueAtTime(note.volume, begin + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.0001, begin + note.duration);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.onended = () => {
          activeVoices.delete(node);
          oscillator.disconnect();
          gain.disconnect();
        };
      }

      soundBusyUntil = now + (buyNow ? 550 : 400);
      for (const node of nodes) {
        node.oscillator.start(node.begin);
        node.oscillator.stop(node.end);
      }
      report(buyNow ? 'buy-now-sound-scheduled' : 'cart-checkout-sound-scheduled');
    } catch (_) {
      for (const node of nodes) {
        activeVoices.delete(node);
        try {
          node.oscillator.stop();
          node.oscillator.disconnect();
          node.gain.disconnect();
        } catch (_) {}
      }
    }
  }

  function selection(form) {
    if (!(form instanceof HTMLFormElement) || form.matches(':invalid')) return null;
    const id = String(form.elements.namedItem('id')?.value || '');
    const quantity = Number(form.elements.namedItem('quantity')?.value || 1);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    return { id, quantity };
  }

  function isCurrent(attempt) {
    if (attempt.generation !== pageGeneration || !attempt.host.isConnected || !attempt.form.isConnected) return false;
    if (performance.now() - attempt.createdAt > 30000 || document.visibilityState !== 'visible') return false;
    const current = selection(attempt.form);
    return current?.id === attempt.selection.id && current.quantity === attempt.selection.quantity;
  }

  function restoreHook(hook) {
    clearTimeout(hook.timeout);
    activeHooks.delete(hook);
    if (pendingHooks.get(hook.source) === hook) pendingHooks.delete(hook.source);
    try {
      if (hook.source.getInitialCart !== hook.wrapper) return;
      if (hook.descriptor) {
        Object.defineProperty(hook.source, 'getInitialCart', hook.descriptor);
      } else {
        delete hook.source.getInitialCart;
      }
    } catch (_) {}
  }

  function onCheckoutResult(attempt, result) {
    if (!isCurrent(attempt) || !result || !Array.isArray(result.errors) || result.errors.length) return;
    if (result.abortReason != null || result.decelerationReason != null) return;
    const cart = result.cart;
    if (!cart || typeof cart.id !== 'string' || !cart.id || typeof cart.checkoutUrl !== 'string') return;
    const checkoutUrl = new URL(cart.checkoutUrl);
    if (checkoutUrl.protocol !== 'https:') return;
    if (!Array.isArray(cart.lineItems) || !cart.lineItems.length) return;
    if (cart.lineItems[0].quantity !== attempt.selection.quantity) return;
    if (cart.lineItems.some(line => !Number.isSafeInteger(line.quantity) || line.quantity < 1)) return;
    report('buy-now-checkout-accepted');
    playChime(true);
  }

  function observeNativeCheckout(source, attempt) {
    if (!isCurrent(attempt) || !source || typeof source.getInitialCart !== 'function') return;
    const previous = pendingHooks.get(source);
    if (previous) restoreHook(previous);
    const original = source.getInitialCart;
    const descriptor = Object.getOwnPropertyDescriptor(source, 'getInitialCart');
    if (descriptor && (!('value' in descriptor) || !descriptor.writable)) return;
    const hook = { source, descriptor, wrapper: null, timeout: undefined };

    hook.wrapper = function (...args) {
      const matches = this === source && args[0] === 'BuyItNow' && !attempt.consumed;
      let shouldObserve = false;
      if (matches) {
        attempt.consumed = true;
        restoreHook(hook);
        try { shouldObserve = isCurrent(attempt); } catch (_) {}
      }
      const result = Reflect.apply(original, this, args);
      if (shouldObserve) {
        try {
          if (result && typeof result.then === 'function') {
            result.then(value => onCheckoutResult(attempt, value), () => {}).catch(() => {});
          }
        } catch (_) {}
      }
      return result;
    };

    try {
      Object.defineProperty(source, 'getInitialCart', descriptor
        ? { ...descriptor, value: hook.wrapper }
        : { configurable: true, writable: true, value: hook.wrapper });
      pendingHooks.set(source, hook);
      activeHooks.add(hook);
      hook.timeout = setTimeout(() => restoreHook(hook), 0);
    } catch (_) {}
  }

  function onBuyNowClick(event) {
    try {
      if (!event.isTrusted || event.defaultPrevented || event.button !== 0) return;
      const path = event.composedPath();
      const host = path.find(node => node instanceof Element && node.matches('shopify-buy-it-now-button'));
      if (!host || path.some(node => node instanceof Element && node.matches('[disabled], [aria-disabled="true"]'))) return;
      const form = host.closest('form');
      const selected = selection(form);
      if (!selected || !host.dataSource || typeof host.dataSource.then !== 'function') return;
      const attempt = {
        host, form, selection: selected, createdAt: performance.now(), generation: pageGeneration, consumed: false,
      };
      armAudio();
      report('buy-now-armed');
      host.dataSource.then(source => observeNativeCheckout(source, attempt)).catch(() => {});
    } catch (_) {}
  }

  function onCartCheckoutIntent(event) {
    try {
      if (!event.isTrusted || event.defaultPrevented || event.button !== 0) return;
      const path = event.composedPath();
      if (path.some(node => node instanceof Element && node.matches('[disabled], [aria-disabled="true"]'))) return;
      const control = path.find(node => node instanceof Element && node.matches(
        'button.cart__checkout-button, button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
      ));
      if (!control) return;
      const form = control.closest('form');
      if (form?.matches(':invalid')) return;
      armAudio();
      playChime(false);
    } catch (_) {}
  }

  document.addEventListener('click', onBuyNowClick, { capture: true, passive: true });
  document.addEventListener('pointerdown', onCartCheckoutIntent, { capture: true, passive: true });
  document.addEventListener('click', onCartCheckoutIntent, { capture: true, passive: true });
  function releasePageAudio() {
    pageGeneration += 1;
    for (const hook of activeHooks) restoreHook(hook);
    stopVoices();
    try { audioContext?.close().catch(() => {}); } catch (_) {}
    audioContext = null;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') releasePageAudio();
  });
  window.addEventListener('pagehide', releasePageAudio);
})();
